import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { 
    CheckCircle, 
    ArrowRight,
    BookOpen,
    Calculator,
    FileText,
    TrendingUp,
    MessageSquare,
    ClipboardCheck
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

// Hero image for this page
const HERO_IMAGE = "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=1200&q=80";

const KattavatPalvelut = () => {
    const services = [
        {
            icon: BookOpen,
            title: "Kirjanpito",
            description: "Kuukausittainen kirjanpito, ALV-ilmoitukset ja raportointi"
        },
        {
            icon: Calculator,
            title: "Palkanlaskenta",
            description: "Palkanmaksu, työnantajamaksut ja vuosi-ilmoitukset"
        },
        {
            icon: FileText,
            title: "Tilinpäätökset",
            description: "Tilinpäätökset ja toimintakertomukset"
        },
        {
            icon: ClipboardCheck,
            title: "Veroilmoitukset",
            description: "Veroilmoitusten laadinta ja toimitus"
        },
        {
            icon: TrendingUp,
            title: "Verosuunnittelu",
            description: "Strateginen verosuunnittelu yrityksellesi"
        },
        {
            icon: MessageSquare,
            title: "Konsultointi",
            description: "Asiantunteva konsultointi veroasioissa"
        }
    ];

    const benefits = [
        "Kokenut ja ammattitaitoinen tiimi",
        "Henkilökohtainen palvelu",
        "Modernit digitaaliset työkalut",
        "Kilpailukykyinen hinnoittelu",
        "Nopea ja luotettava palvelu",
        "Joustava yhteistyö"
    ];

    return (
        <div className="min-h-screen bg-white">
            {/* Hero Section */}
            <section className="relative bg-[#1a1a1a] text-white overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-[#1a1a1a] to-[#1a1a1a]/80"></div>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32 relative z-10">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <motion.div
                            initial={{ opacity: 0, x: -30 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6 }}
                        >
                            <span className="text-sm uppercase tracking-[0.2em] font-medium text-green-400 mb-4 block">
                                Ratkaisut
                            </span>
                            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight mb-6">
                                Kattavat tilitoimistopalvelut
                            </h1>
                            <p className="text-lg text-white/80 leading-relaxed mb-8 max-w-xl">
                                Tarjoamme kaikki taloushallinnon palvelut saman katon alta. 
                                Saat kattavat tilitoimistopalvelut, joiden avulla voit keskittyä 
                                ydinliiketoimintaasi.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4">
                                <Link to="/#yhteystiedot">
                                    <Button className="bg-white text-[#1a1a1a] hover:bg-slate-100 px-8 py-3 rounded-full text-base">
                                        Pyydä tarjous
                                        <ArrowRight className="ml-2 h-4 w-4" />
                                    </Button>
                                </Link>
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, x: 30 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                            className="hidden lg:block"
                        >
                            <img 
                                src={HERO_IMAGE}
                                alt="Tilitoimistopalvelut"
                                className="rounded-2xl shadow-2xl"
                            />
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Services Grid */}
            <section className="py-20 bg-[#F8FAFC]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <motion.div 
                        className="text-center mb-16"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                    >
                        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-slate-900 mb-4">
                            Palvelumme sinulle
                        </h2>
                        <p className="text-slate-600 max-w-2xl mx-auto">
                            Kaikki mitä tarvitset yrityksesi taloushallintoon
                        </p>
                    </motion.div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {services.map((service, index) => {
                            const Icon = service.icon;
                            return (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: index * 0.1 }}
                                >
                                    <Card className="h-full hover:shadow-lg transition-shadow border-0 bg-white">
                                        <CardContent className="p-6">
                                            <div className="bg-[#22C55E]/10 w-12 h-12 rounded-xl flex items-center justify-center mb-4">
                                                <Icon className="h-6 w-6 text-[#22C55E]" />
                                            </div>
                                            <h3 className="text-xl font-semibold text-slate-800 mb-2">
                                                {service.title}
                                            </h3>
                                            <p className="text-slate-600">
                                                {service.description}
                                            </p>
                                        </CardContent>
                                    </Card>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Benefits Section */}
            <section className="py-20 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <motion.div
                            initial={{ opacity: 0, x: -30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                        >
                            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-slate-900 mb-6">
                                Miksi valita meidät?
                            </h2>
                            <p className="text-slate-600 mb-8">
                                Kuulas Tilitoimisto tarjoaa laadukasta ja henkilökohtaista palvelua. 
                                Meillä saat kaiken tarvitsemasi taloushallintoon.
                            </p>
                            <ul className="space-y-4">
                                {benefits.map((benefit, index) => (
                                    <motion.li 
                                        key={index}
                                        className="flex items-center gap-3"
                                        initial={{ opacity: 0, x: -20 }}
                                        whileInView={{ opacity: 1, x: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: index * 0.1 }}
                                    >
                                        <CheckCircle className="h-5 w-5 text-[#22C55E] flex-shrink-0" />
                                        <span className="text-slate-700">{benefit}</span>
                                    </motion.li>
                                ))}
                            </ul>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, x: 30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            className="bg-[#1a1a1a] rounded-2xl p-8 text-white"
                        >
                            <h3 className="text-2xl font-semibold mb-4">Ota yhteyttä</h3>
                            <p className="text-white/80 mb-6">
                                Kerro meille tarpeistasi ja saat räätälöidyn tarjouksen.
                            </p>
                            <Link to="/#yhteystiedot">
                                <Button className="w-full bg-white text-[#1a1a1a] hover:bg-slate-100 rounded-full">
                                    Pyydä tarjous
                                    <ArrowRight className="ml-2 h-4 w-4" />
                                </Button>
                            </Link>
                        </motion.div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default KattavatPalvelut;
